/**
 * VerifyPhotoModule — public face-comparison module.
 *
 * Flow:
 *   1. Visitor drops / selects two images (JPEG/PNG/WEBP, up to 8 MB each).
 *   2. Each image POSTs to `/api/verify-photo/upload` immediately, so the
 *      backend has a stable URL to reference and we don't re-upload on
 *      every compare click.
 *   3. `Compare` CTA calls `/api/verify-photo/compare` with both URLs and
 *      renders the likeness percentage + neutral 1-sentence note.
 *   4. Auto-run: if both slots are populated AND we don't have a cached
 *      result yet for this visitor's session, the compare fires once on
 *      mount so returning users see a score without touching the CTA again.
 *
 * Persistence is deliberately client-side (localStorage per module instance).
 * The backend caches the *pair-hash → result* for cheap re-fetch, but the
 * visitor's uploaded URLs live only in their browser so refreshing on a
 * different device is a fresh start.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { useShopperAuth } from "@/context/ShopperAuthContext";
import { Button } from "@/components/ui/button";
import {
  Upload, Loader2, RefreshCw, XCircle, ImageIcon, AlertTriangle,
  History, Camera, BookOpen, IdCard, CreditCard,
} from "lucide-react";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";

const API = getApiUrl();

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"];

function _storageKey(instanceKey) {
  return `verify-photo:${instanceKey || "default"}`;
}

function _readStored(instanceKey) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(_storageKey(instanceKey));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function _writeStored(instanceKey, value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(_storageKey(instanceKey), JSON.stringify(value));
  } catch { /* quota full — best effort only */ }
}

/**
 * CameraCaptureModal — desktop-friendly live camera preview.
 *
 * Opens `navigator.mediaDevices.getUserMedia({video:{facingMode:'user'}})`
 * and streams into a `<video>` element. Clicking "Capture" grabs the
 * current frame off the video via a hidden `<canvas>`, converts it to
 * a PNG File, and hands it back through `onCapture(file)`. Mobile
 * browsers also honour getUserMedia so a single implementation works
 * on both — no need for the `capture="user"` file-input fallback here.
 *
 * The stream is torn down on close so the camera light doesn't stay on.
 */
function CameraCaptureModal({ open, onClose, onCapture }) {
  useLockBodyScroll(open);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  // Two-step capture: snap first draws to a canvas and exposes a preview
  // so the agent can review before committing. `pending` holds the
  // captured File + preview data URL while the user decides between
  // Retake and Use this photo.
  const [pending, setPending] = useState(null);
  // Countdown state — null when idle, 3→2→1 while the timer runs. When
  // it hits 0 we fire `snap()`. Kept as a ref-mirror so the interval
  // callback can decide when to stop without stale closures.
  const [countdown, setCountdown] = useState(null);
  const countdownTimer = useRef(null);

  const clearCountdown = () => {
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
    setCountdown(null);
  };

  const stopStream = () => {
    const s = streamRef.current;
    if (s) s.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const startStream = useCallback(async () => {
    setError("");
    setStarting(true);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("This browser doesn't support live camera access. Try 'Upload' instead.");
      setStarting(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => { /* autoplay policies */ });
      }
    } catch (e) {
      setError(
        e?.name === "NotAllowedError"
          ? "Camera permission was denied. Enable it in your browser settings and try again."
          : "Could not start the camera. Try 'Upload' instead."
      );
    } finally {
      setStarting(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setPending(null);
    clearCountdown();
    (async () => {
      await startStream();
      if (cancelled) stopStream();
    })();
    return () => {
      cancelled = true;
      stopStream();
      clearCountdown();
    };
  }, [open, startStream]);

  const snap = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    // Mirror the frame back to normal orientation — the preview is
    // mirrored so it feels natural, but the saved image should read
    // the same way a phone photo would.
    ctx.save();
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, w, h);
    ctx.restore();
    canvas.toBlob((blob) => {
      if (!blob) { toast.error("Capture failed"); return; }
      const file = new File([blob], `selfie_${Date.now()}.png`, { type: "image/png" });
      const previewUrl = URL.createObjectURL(blob);
      setPending({ file, previewUrl });
      // Free the camera while the agent reviews — restart on Retake.
      stopStream();
    }, "image/png", 0.92);
  };

  const retake = async () => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
    clearCountdown();
    await startStream();
  };

  const commit = () => {
    if (!pending) return;
    onCapture(pending.file);
    URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
    onClose();
  };

  // Kick off a 3-2-1 countdown then fire `snap()` at zero. Cancels any
  // in-flight timer so a double-click doesn't stack intervals.
  const startCountdown = () => {
    clearCountdown();
    setCountdown(3);
    countdownTimer.current = setInterval(() => {
      setCountdown((c) => {
        if (c === null) return c;
        if (c <= 1) {
          clearInterval(countdownTimer.current);
          countdownTimer.current = null;
          // Snap on the next tick so the "0" (or blank) has a chance to
          // paint and doesn't leave a "1" hanging on screen.
          setTimeout(() => { snap(); }, 0);
          return null;
        }
        return c - 1;
      });
    }, 1000);
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4"
      data-testid="camera-capture-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4" />
            <h3 className="text-sm font-semibold">
              {pending ? "Review your selfie" : "Take selfie"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-muted rounded"
            aria-label="Close camera"
            data-testid="camera-close-btn"
          >
            <XCircle className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
        <div className="relative bg-black aspect-video">
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center p-6 text-white text-sm text-center">
              {error}
            </div>
          ) : pending ? (
            <img
              src={pending.previewUrl}
              alt="Captured selfie"
              className="w-full h-full object-cover"
              data-testid="camera-preview-image"
            />
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: "scaleX(-1)" }}
                data-testid="camera-video"
              />
              {/* Face-oval guide — a soft dashed ellipse the agent lines
                  their face up with. Sized as a portion of the video
                  aspect ratio so it stays proportional across viewports.
                  Non-interactive so it never blocks the shutter. */}
              {!starting && !error && (
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  viewBox="0 0 100 56"
                  preserveAspectRatio="none"
                  data-testid="camera-face-guide"
                >
                  <ellipse
                    cx="50" cy="28" rx="17" ry="24"
                    fill="none"
                    stroke="rgba(255,255,255,0.85)"
                    strokeWidth="0.5"
                    strokeDasharray="1.2 1.2"
                  />
                  <text
                    x="50" y="54"
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.9)"
                    style={{ fontSize: "2.2px", fontWeight: 500 }}
                  >
                    Line up your face in the oval
                  </text>
                </svg>
              )}
              {starting && (
                <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting camera…
                </div>
              )}
              {countdown !== null && (
                <div
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  data-testid="camera-countdown-overlay"
                >
                  <span
                    key={countdown}
                    className="text-white font-bold drop-shadow-lg animate-pulse"
                    style={{ fontSize: "8rem", lineHeight: 1 }}
                    data-testid="camera-countdown-number"
                  >
                    {countdown}
                  </span>
                </div>
              )}
            </>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          {pending ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={retake}
                data-testid="camera-retake-btn"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retake
              </Button>
              <Button
                size="sm"
                onClick={commit}
                data-testid="camera-use-btn"
              >
                Use this photo
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button
                variant="outline"
                size="sm"
                onClick={startCountdown}
                disabled={!!error || starting || countdown !== null}
                data-testid="camera-timer-btn"
                title="3-2-1 countdown before capture"
              >
                <History className="h-3.5 w-3.5 mr-1" /> 3s timer
              </Button>
              <Button
                size="sm"
                onClick={snap}
                disabled={!!error || starting || countdown !== null}
                data-testid="camera-snap-btn"
              >
                <Camera className="h-3.5 w-3.5 mr-1" /> Capture
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * UploadSlot — one of the two drop targets. Contained here so the outer
 * module component stays focused on comparison flow.
 */
function UploadSlot({ label, hint, url, previewSrc, onUploaded, onClear, busy, allowCamera, badges, selectedBadge, onSelectBadge }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const validate = (file) => {
    if (!file) return "No file";
    if (!ALLOWED_MIME.includes(file.type)) return "Only JPEG, PNG or WEBP";
    if (file.size > MAX_UPLOAD_BYTES) return "Image too large (max 8 MB)";
    return null;
  };

  const upload = async (file) => {
    const err = validate(file);
    if (err) { toast.error(err); return; }
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/verify-photo/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url, file);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) upload(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };

  return (
    <div
      className={`relative border-2 border-dashed rounded-lg aspect-square flex items-center justify-center bg-muted/20 overflow-hidden transition-colors ${
        dragging ? "border-primary bg-primary/5" : "border-border"
      }`}
      onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
      onDragOver={(e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"; }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      data-testid={`verify-slot-${label.toLowerCase()}`}
    >
      {url ? (
        <>
          <img
            src={previewSrc || `${API.replace(/\/api$/, "")}${url}`}
            alt={label}
            className="w-full h-full object-contain p-1"
            data-testid={`verify-preview-${label.toLowerCase()}`}
          />
          <button
            type="button"
            onClick={onClear}
            disabled={busy}
            className="absolute top-2 right-2 p-1 bg-white/90 rounded-full shadow hover:bg-white disabled:opacity-50"
            aria-label={`Remove ${label}`}
            data-testid={`verify-clear-${label.toLowerCase()}`}
          >
            <XCircle className="h-5 w-5 text-red-600" />
          </button>
          {/* AI-processing overlay — shown on both slots while a compare
              is in flight so the agent sees it's actively analysing these
              exact two images, not just a generic button spinner. */}
          {busy && (
            <div
              className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-2 text-white"
              data-testid={`verify-processing-${label.toLowerCase()}`}
            >
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-xs font-medium tracking-wide">AI analysing…</span>
            </div>
          )}
        </>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground p-4 text-center">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex flex-col items-center gap-2 hover:text-foreground"
            data-testid={`verify-upload-${label.toLowerCase()}`}
          >
            <ImageIcon className="h-8 w-8" />
            <span className="text-sm font-medium text-foreground">{label}</span>
            {hint && (
              <span className="text-xs leading-snug">{hint}</span>
            )}
            <span className="text-xs">Click or drop an image</span>
            <span className="text-[10px] uppercase tracking-wider">JPEG · PNG · WEBP</span>
          </button>
          {/* Supported-document badges (Photo A only) — click a badge to
              hint the ID type; the backend uses this to flag mismatches
              from the AI (e.g. the user says "Passport" but the image is
              clearly a driving licence). */}
          {badges && badges.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-1.5 pt-1"
                 data-testid={`verify-badges-${label.toLowerCase()}`}>
              {badges.map((b) => {
                const Icon = b.icon;
                const active = selectedBadge === b.key;
                const clickable = typeof onSelectBadge === "function";
                return (
                  <button
                    key={b.key}
                    type="button"
                    onClick={clickable ? (e) => {
                      e.stopPropagation();
                      // Second click on the active badge clears the hint.
                      onSelectBadge(active ? null : b.key);
                    } : undefined}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-foreground/70 hover:bg-muted/70"
                    } ${clickable ? "cursor-pointer" : "cursor-default"}`}
                    title={b.title || b.label}
                    data-testid={`verify-badge-${b.key}`}
                    aria-pressed={active}
                  >
                    <Icon className="h-3 w-3" />
                    {b.label}
                  </button>
                );
              })}
            </div>
          )}
          {/* "Take photo" CTA (Photo B only) — opens an in-page live camera
              preview using getUserMedia. Works on both desktop and mobile
              so agents don't need to hop between devices to snap a selfie. */}
          {allowCamera && (
            <button
              type="button"
              onClick={() => setCameraOpen(true)}
              className="inline-flex items-center gap-1.5 mt-1 px-3 py-1 text-xs font-medium rounded-full bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              data-testid={`verify-camera-${label.toLowerCase()}`}
            >
              <Camera className="h-3.5 w-3.5" /> Take photo
            </button>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={onFile}
      />
      {allowCamera && (
        <CameraCaptureModal
          open={cameraOpen}
          onClose={() => setCameraOpen(false)}
          onCapture={(file) => upload(file)}
        />
      )}
    </div>
  );
}

export default function VerifyPhotoModule({ config = {} }) {
  const {
    heading = "Verify Photo",
    subheading = "Upload a photo ID document and a selfie — we'll confirm both look right and check they show the same person.",
    instance_id, // when set by the CMS wrapper we use it to key localStorage
    // When the module is mounted by master admin operating on behalf of a
    // specific agent (Trading > Agents > Documents > Verify), the caller
    // threads the agent_id through so every backend call carries it as a
    // query param. Undefined for the normal signed-in-user flow.
    agent_id: onBehalfOfAgentId,
  } = config;
  const agentQuery = onBehalfOfAgentId ? { agent_id: onBehalfOfAgentId } : {};

  // Auth gate. `useShopperAuth` returns the Emergent-managed shopper session.
  // Property-admin (agent) sessions live on a separate cookie the browser
  // sends automatically; the backend `verify-photo/session` endpoint
  // resolves either one. `axios({withCredentials})` is required across every
  // call so the agent cookie rides along on same-origin requests.
  const shopperAuth = useShopperAuth();
  const [authState, setAuthState] = useState({ loading: true, signed_in: false, scope: null, name: null });

  const [imageA, setImageA] = useState("");
  const [imageB, setImageB] = useState("");
  // Local blob preview for each slot — the server-stored URL
  // (`/uploads/verify_*`) requires a signed-in session to view (ID
  // documents stay protected), so anonymous visitors would otherwise see a
  // broken <img>. We render straight from the just-picked File instead;
  // `imageA`/`imageB` (the real server URL) are still what's sent to
  // `/verify-photo/compare`.
  const [previewA, setPreviewA] = useState("");
  const [previewB, setPreviewB] = useState("");
  const [result, setResult] = useState(null); // {likeness_percent, notes, faces_detected, compared_at}
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState([]);
  const [showLog, setShowLog] = useState(false);
  // Optional agent-picked hint for what ID document IMAGE A should be. When
  // set (e.g. "passport"), the compare endpoint asks the LLM to flag a
  // mismatch (agent said passport but the AI sees a driving licence, etc.).
  const [selectedIdType, setSelectedIdType] = useState(null);

  // Probe session on mount and whenever shopper auth changes (SSO redirect
  // returns fire the shopper hook, but the agent cookie is invisible to
  // the frontend — the backend probe is the only reliable signal).
  useEffect(() => {
    let alive = true;
    const probe = async () => {
      try {
        const { data } = await axios.get(`${API}/verify-photo/session`, {
          params: agentQuery,
          withCredentials: true,
        });
        if (alive) setAuthState({ loading: false, ...data });
      } catch {
        if (alive) setAuthState({ loading: false, signed_in: false, scope: null, name: null });
      }
    };
    probe();
    return () => { alive = false; };
  }, [shopperAuth?.shopper?.shopper_id]);

  const refreshAttempts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/verify-photo/attempts`, {
        params: agentQuery,
        withCredentials: true,
      });
      setAttempts(data.attempts || []);
    } catch {
      // Silently — an empty log is a valid state on first sign-in.
      setAttempts([]);
    }
  }, [onBehalfOfAgentId]);
  useEffect(() => {
    if (authState.signed_in) refreshAttempts();
  }, [authState.signed_in, refreshAttempts]);

  // Hydrate from localStorage on first mount so a refresh keeps state.
  useEffect(() => {
    const stored = _readStored(instance_id);
    if (stored) {
      setImageA(stored.imageA || "");
      setImageB(stored.imageB || "");
      setResult(stored.result || null);
      setSelectedIdType(stored.selectedIdType || null);
    }
  }, [instance_id]);

  // Persist changes so refresh + auto-compare know what to do.
  useEffect(() => {
    _writeStored(instance_id, { imageA, imageB, result, selectedIdType });
  }, [instance_id, imageA, imageB, result, selectedIdType]);

  const inFlightRef = useRef(false);
  // Ref-mirror of `runCompare` so we can chain a second call from inside
  // the same handler (e.g. auto-rerun after a server-side rotation) without
  // fighting React's function identity for the useCallback closure.
  const runCompareRef = useRef(null);
  const runCompare = useCallback(async () => {
    if (!imageA || !imageB) return;
    // Guard against a double-fire — the auto-run useEffect and the
    // "Compare with AI" button both call this. Without this ref, a user
    // clicking the button in the ~one frame window before `comparing`
    // flips to true would trigger a duplicate LLM call + duplicate
    // ID Documents save.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setComparing(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/verify-photo/compare`, {
        image_a_url: imageA,
        image_b_url: imageB,
        expected_id_type: selectedIdType || null,
      }, { params: agentQuery, withCredentials: true });
      // When the backend auto-rotated IMAGE A, don't render this
      // intermediate result — the LLM analysed the pre-rotation bytes
      // and the cache row was already dropped server-side. Toast the
      // user and re-fire the compare against the corrected file.
      if (data.rotation_applied) {
        toast.info(
          `ID photo auto-rotated ${data.rotation_applied}° — re-analysing…`
        );
        inFlightRef.current = false;
        setComparing(false);
        // Small delay lets state settle + gives the disk write a beat.
        setTimeout(() => {
          const rerun = runCompareRef.current;
          if (rerun) rerun();
        }, 150);
        return;
      }
      setResult(data);
      // Refresh the personal log so the new attempt appears immediately;
      // don't await — this shouldn't block the score reveal.
      refreshAttempts();
      if (data.rejected) {
        toast.error(data.rejection_reason
          || "Rejected — Photo A must be a photo ID document and Photo B must be a selfie",
          data.saved_to_id_docs ? { description: "Both photos were still saved to your ID Documents folder for review." } : undefined);
      } else if (data.likeness_percent >= 70 && data.saved_to_id_docs) {
        toast.success("Match ≥ 70% — both photos saved to your ID Documents folder");
      } else if (data.saved_to_id_docs) {
        toast.info("Both photos saved to your ID Documents folder for review");
      }
    } catch (e) {
      if (e.response?.status === 401) {
        // Session expired mid-flow — kick the shopper back through SSO.
        toast.error("Sign in expired — please sign in again");
        setAuthState({ loading: false, signed_in: false, scope: null, name: null });
        return;
      }
      const msg = e.response?.data?.detail || "Comparison failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setComparing(false);
      inFlightRef.current = false;
    }
  }, [imageA, imageB, refreshAttempts, agentQuery, selectedIdType]);

  // Keep the ref pointing at the latest runCompare so the auto-rerun
  // path always invokes the current closure (fresh imageA/imageB, etc.).
  useEffect(() => { runCompareRef.current = runCompare; }, [runCompare]);

  // Auto-run: both images uploaded, no result yet → fire once.
  // The `result` guard means swapping an image (which clears result) will
  // trigger a fresh compare, but a page refresh with a cached result will
  // not re-bill the LLM.
  const autoTriggered = useRef(false);
  useEffect(() => {
    if (autoTriggered.current) return;
    if (imageA && imageB && !result && !comparing) {
      autoTriggered.current = true;
      runCompare();
    }
  }, [imageA, imageB, result, comparing, runCompare]);

  const clearImage = (which) => {
    if (which === "A") { setImageA(""); setPreviewA((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; }); }
    else { setImageB(""); setPreviewB((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; }); }
    setResult(null);
    autoTriggered.current = false; // allow next auto-fire once B (or A) filled
  };

  const setUploadedImage = (which, url, file) => {
    const preview = file ? URL.createObjectURL(file) : "";
    if (which === "A") {
      setImageA(url);
      setPreviewA((prev) => { if (prev) URL.revokeObjectURL(prev); return preview; });
    } else {
      setImageB(url);
      setPreviewB((prev) => { if (prev) URL.revokeObjectURL(prev); return preview; });
    }
    setResult(null); // stale — force a new compare
    autoTriggered.current = false;
  };

  const canCompare = imageA && imageB && !comparing;

  // Colour the score bar so a quick glance conveys the verdict.
  const score = result?.likeness_percent ?? 0;
  const bandColor =
    score >= 75 ? "bg-emerald-500" :
    score >= 45 ? "bg-amber-500" :
    "bg-red-500";

  return (
    <section className="py-12 px-4" data-testid="verify-photo-module">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Rendered as <div>, not <header>: the global Site Styles rule
            `[data-public-site] header { background: ... }` (meant for the
            page-level HeaderModule) would otherwise repaint this intro band
            with the site's header colours. See the same fix applied to
            PropertyArticleModule. */}
        <div className="verify-photo-header text-center space-y-2">
          <h2 className="text-3xl font-semibold" style={{ fontFamily: "var(--font-heading, inherit)" }}>
            {heading}
          </h2>
          {subheading && <p className="text-sm text-muted-foreground">{subheading}</p>}
        </div>

        {/* Sign-in is optional — used only to personalise the greeting and
            show a saved verification-history panel below. The compare tool
            itself works immediately for anonymous visitors. */}
        {authState.loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {authState.signed_in && authState.name && (
              <p
                className="text-center text-xs text-muted-foreground -mt-3"
                data-testid="verify-signed-as"
              >
                Signed in as <strong>{authState.name}</strong>
                {authState.scope === "agent" && " · Agent"}
              </p>
            )}

        <div className="grid grid-cols-2 gap-4">
          <UploadSlot
            label="Photo A"
            hint="Photo ID document (passport, driving licence, ID card)"
            badges={[
              { key: "passport", icon: BookOpen, label: "Passport" },
              { key: "licence", icon: IdCard, label: "Driving licence" },
              { key: "id-card", icon: CreditCard, label: "ID card" },
            ]}
            selectedBadge={selectedIdType}
            onSelectBadge={setSelectedIdType}
            url={imageA}
            previewSrc={previewA}
            onUploaded={(url, file) => setUploadedImage("A", url, file)}
            onClear={() => clearImage("A")}
            busy={comparing}
          />
          <UploadSlot
            label="Photo B"
            hint="Selfie of the same person"
            allowCamera
            url={imageB}
            previewSrc={previewB}
            onUploaded={(url, file) => setUploadedImage("B", url, file)}
            onClear={() => clearImage("B")}
            busy={comparing}
          />
        </div>

        <div className="flex items-center justify-center gap-3">
          <Button
            onClick={runCompare}
            disabled={!canCompare}
            data-testid="verify-compare-btn"
            className="min-w-[200px]"
          >
            {comparing ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Comparing…</>
            ) : result ? (
              <><RefreshCw className="mr-2 h-4 w-4" /> Compare again</>
            ) : (
              <><Upload className="mr-2 h-4 w-4" /> Compare with AI</>
            )}
          </Button>
        </div>

        {/* Result panel */}
        {result && (
          <div
            className="border border-border rounded-lg bg-white p-6 space-y-3"
            data-testid="verify-result"
          >
            {result.rejected ? (
              <div className="flex items-start gap-3" data-testid="verify-rejected">
                <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">
                    {!result.image_a_is_photo_id && !result.image_b_is_selfie
                      ? "Both photos are wrong"
                      : !result.image_a_is_photo_id
                        ? "Photo A must be a photo ID document"
                        : "Photo B must be a selfie"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {result.rejection_reason
                      || "We could not verify these images. Photo A must be a passport, driving licence or ID card. Photo B must be a live selfie of the same person."}
                  </p>
                </div>
              </div>
            ) : result.faces_detected ? (
              <>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm uppercase tracking-wider text-muted-foreground">
                    Likeness
                  </span>
                  <span className="text-4xl font-bold" data-testid="verify-score">
                    {score}%
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full ${bandColor} transition-all duration-500`}
                    style={{ width: `${score}%` }}
                  />
                </div>
                {result.notes && (
                  <p className="text-sm text-muted-foreground pt-2 border-t border-border">
                    {result.notes}
                  </p>
                )}
                {result.cached && (
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Result served from cache
                  </p>
                )}
              </>
            ) : (
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">No clear face detected</p>
                  <p className="text-sm text-muted-foreground">
                    {result.notes || "Try photos where each person's face is fully visible."}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 text-center" data-testid="verify-error">
            {error}
          </div>
        )}

        {/* Verification history — collapsed by default to keep the module
            compact when the log has grown long. Rendered at the bottom so
            first-time users focus on the upload flow above. */}
        {authState.signed_in && (
          <div className="border border-border rounded-lg bg-white overflow-hidden" data-testid="verify-log">
            <button
              type="button"
              onClick={() => setShowLog((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 text-sm font-medium"
              data-testid="verify-log-toggle"
            >
              <span className="flex items-center gap-2">
                <History className="h-4 w-4" />
                Verification history
                <span className="text-xs text-muted-foreground">({attempts.length})</span>
              </span>
              <span className="text-xs text-muted-foreground">{showLog ? "Hide" : "Show"}</span>
            </button>
            {showLog && (
              <div className="border-t border-border">
                {attempts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No attempts yet — run your first comparison above.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="text-left py-2 px-3">When</th>
                        <th className="text-left py-2 px-3">Score</th>
                        <th className="text-left py-2 px-3 hidden md:table-cell">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attempts.map((a) => {
                        const pct = a.likeness_percent ?? 0;
                        const band = pct >= 70 ? "bg-emerald-500" : pct >= 45 ? "bg-amber-500" : "bg-red-500";
                        return (
                          <tr key={a.attempt_id} className="border-t border-border" data-testid={`verify-log-row-${a.attempt_id}`}>
                            <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">
                              {new Date(a.created_at).toLocaleString(undefined, {
                                dateStyle: "medium", timeStyle: "short",
                              })}
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <span className={`inline-block w-2 h-2 rounded-full ${band}`} />
                                <span className="font-medium">{pct}%</span>
                                {a.cached && <span className="text-[10px] uppercase text-muted-foreground">cache</span>}
                              </div>
                            </td>
                            <td className="py-2 px-3 text-xs text-muted-foreground hidden md:table-cell">
                              {a.notes || (a.faces_detected ? "" : "No clear face")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}
          </>
        )}
      </div>
    </section>
  );
}
